import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { AppServiceHelperService } from '../../../../../services/app-service-helper.service';

/**
 * SignatureAndLicense service: Handles Signatures and Licenses
 */
@Injectable({
  providedIn: 'root'
})
export class SignatureAndLicenseService {
  /**
   * Widget API Resource
   */
  private _widgetResource: string;

  /**
   * The selecteded data observable
   */
  public selectedData$ = new BehaviorSubject<any[]>(null);

  /**
   * The widget's choices data observable
   */
  public choices$ = new BehaviorSubject<any[]>(null);

  /**
   * The widget data subject
   */
  public widgetDataLoaded$ = new BehaviorSubject<boolean>(false);

  /**
   * Is widget data loaded?
   */
  private widgetDataLoaded = false;

  /**
   * Is licenses dropdown disabled?
   */
  public isDisabledLicenses: boolean[] = [true, true, true, true];

  /**
   * Is licenses dropdown disabled?
   */
  public isDisabledUsers: boolean[] = [false, false, false, false];

  /**
   * Initial data
   */
  private initialData: any[] = null;

  /**
   * Constructor
   * @param httpClient
   * @param appServiceHelper
   */
  constructor(private httpClient: HttpClient,
    private appServiceHelper: AppServiceHelperService) {
    this._widgetResource = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportwidgets/signatureAndLicense');
  }

  /**
   * Report Id
   */
  private reportId: number = null;

  /**
   * Default dropdown item for user
   */
  public defaultItemUsers: { userName: string; userId: number } = {
    userName: "Select user",
    userId: null,
  };

  /**
   * Default dropdown item for license
   */
  public defaultItemLicenses: { licenseName: string } = {
    licenseName: "Select license"
  };

  /**
   * Licenses data (filtered by user)
   */
  public dataResultLicenses: Array<Array<{
    licenseName: string;
    userId: number;
  }>> = [[], [], [], []];

  /**
   * Users data
   */
  public dataUsers: Array<Array<{ userName: string; userId: number }>> = [[], [], [], []];

  /**
   * Licenses data
   */
  public dataLicenses: Array<Array<{
    licenseName: string;
    userId: number;
  }>> = [[], [], [], []];


  /**
   * Selected user
   */
  public selectedUser: Array<{ userName: string; userId: number }>;

  /**
   * Selected license
   */
  public selectedLicense: Array<{ licenseName: string; }>;

  /**
   * Handle User change
  * @param value {any} New selected user
   */
  public handleUserChange(index: number, value) {
    this.selectedUser[index] = value;
    this.selectedLicense[index] = undefined;

    if (value.userId === this.defaultItemUsers.userId) {
      this.isDisabledLicenses[index] = true;
      this.dataResultLicenses[index] = [];
    } else {
      this.isDisabledLicenses[index] = false;
      this.dataResultLicenses[index] = this.dataLicenses[index].filter((s) => s.userId === value.userId);
    }
  }

  /**
   * Handle License change
  * @param value {any} New selected licence
   */
  public handleLicenseChange(index: number, value) {
    this.selectedLicense[index] = value;
    if (this.selectedLicense[index] && this.selectedLicense[index].licenseName !== this.defaultItemLicenses.licenseName) {
      this.loadSelection(this.reportId, index, this.selectedUser[index].userId, this.selectedLicense[index].licenseName);
    }
  }

  /**
   * Init service
  * @param reportId {number} Report Id
   */
  public init(reportId: number) {
    if (this.reportId) {
      //Don't double init
      return;
    }
    this.isDisabledUsers = [false, false, false, false];
    this.initialData = null;
    this.reportId = reportId;

    this.selectedUser = [this.defaultItemUsers, this.defaultItemUsers, this.defaultItemUsers, this.defaultItemUsers];
    this.selectedLicense = [this.defaultItemLicenses, this.defaultItemLicenses, this.defaultItemLicenses, this.defaultItemLicenses];
  }

  /**
   * Sets initial data
  * @param data {any} Initial data
   */
  public setInitialData(data: any[]) {
    if (!this.initialData) {
      //No double execution
      this.initialData = data;

      //Load the choices
      this.loadChoicesByReport(this.reportId).pipe(take(1)).subscribe((choices: any[]) => {
        //Assign choices
        this.assignChoices(choices, data);
      });
    }
  }

  /**
   * Assign the possible choices
   * @param choices
   * @param data
   */
  private assignChoices(choices: any[], data: any[]): void {
    this.choices$.next(choices);
    for (let i = 0; i < choices.length; ++i) {
      const choicesForLayout = choices[i];
      this.assignData(choicesForLayout, i);

      //Assign data
      if (this.reportId) {
        this.selectedUser[i] = { userName: data[i].last_name + ', ' + data[i].first_name, userId: data[i].id };
        this.dataResultLicenses[i] = this.dataLicenses[i].filter((s) => s.userId === data[i].id);
        this.selectedLicense[i] = { licenseName: data[i].selectedLicense };
        this.isDisabledLicenses[i] = false;
        this.isDisabledUsers[i] = false;
      }
      else {
        this.isDisabledLicenses[i] = true;
        this.isDisabledUsers[i] = true;
      }
    }

    this.selectedData$.next(data);

    if (!this.widgetDataLoaded) {
      this.widgetDataLoaded = true;
      this.widgetDataLoaded$.next(this.widgetDataLoaded);
    }
  }

  /**
   * Assign the data
   * @param choicesForLayout
   * @param i
   */
  private assignData(choicesForLayout: any, i: number): void {
    for (const entry of choicesForLayout) {
      this.dataUsers[i].push({ userName: entry.last_name + ', ' + entry.first_name, userId: entry.id });

      for (const license of entry.licenses) {
        this.dataLicenses[i].push({ licenseName: license, userId: entry.id });
      }
    }
  }

  /**
   * Loads choices from the API
   */
  public loadChoices() {
    const url = `${this._widgetResource}/choices`;
    return this.httpClient.get(url);
  }

  /**
   * Loads choices from the API
   */
  public loadChoicesByReport(reportId: number) {
    const url = `${this._widgetResource}/choices?reportId=${reportId}`;
    return this.httpClient.get(url);
  }

  /**
   * Loads selection data from the API
   */
  public loadSelection(reportId: number, index: number, userId: number, selectedState: string) {
    const url = `${this._widgetResource}/selection?reportId=${reportId}&userId=${userId}&selectedState=${selectedState}`;
    return this.httpClient.get(url).pipe(take(1)).subscribe((data: any) => {
      let currentData = this.selectedData$.value;
      if (!currentData) {
        currentData = [null, null, null, null];
      }
      currentData[index] = data;
      this.selectedData$.next(currentData);
    });
  }
}
